/**
 * ADR-0047: the LIFECYCLE around `startTelegramIngest` for opencode agents.
 *
 * `telegram-ingest.ts` owns the long-poll loop itself; this owns when an agent
 * has one. The two are split because the loop is pure transport + governance
 * (testable against fakes, no filesystem, no manager state) whereas this half is
 * inherently stateful: one live poller per agent, restarted on rotation, stopped
 * on teardown, and seeded from a durable on-disk cursor.
 *
 * This is the Telegram analogue of `opencode-slack-ingest.ts` and follows it
 * exactly: read back the materialized channel env via the opencode adapter's
 * `readChannelServerEnv` (one source of truth, survives a manager restart),
 * fingerprint the resolved params so a token/policy change restarts the loop
 * while an unchanged config is a cheap no-op per poll tick, and inject via
 * `injectOpencodeMessage` (which resolves the live port/password per call, so a
 * server respawn does not need a restart).
 *
 * The one addition over the Slack wrapper is the durable getUpdates cursor:
 * Telegram is long-poll with an offset, so a manager restart on an in-memory
 * cursor would re-fetch still-unconfirmed updates and re-deliver them. A
 * file-backed offset store (mirroring packages/mcp's telegram-offset-store,
 * re-implemented here because apps/cli must not import packages/mcp) seeds and
 * persists the cursor across restarts.
 */

import { createHash } from 'node:crypto';
import { existsSync, mkdirSync, readFileSync, renameSync, unlinkSync, writeFileSync } from 'node:fs';
import { randomUUID } from 'node:crypto';
import { join } from 'node:path';

import { getFramework } from '@augmented/core';
import { readChannelServerEnv } from '@augmented/core/provisioning/frameworks/opencode/index.js';

import { injectOpencodeMessage } from './opencode-session.js';
import {
  startTelegramIngest,
  resolveTelegramIngestParamsFromEnv,
  resolveTelegramBotIdentity,
  type TelegramIngestHandle,
  type TelegramIngestParams,
  type TelegramOffsetStore,
  type FetchLike,
} from './telegram-ingest.js';

interface IngestEntry {
  /** Fingerprint of the params the live loop was started with. */
  key: string;
  handle: TelegramIngestHandle;
  /**
   * True when the loop was started before `getMe` resolved the bot's identity
   * (degraded peer classification). While true, later ensure ticks keep retrying
   * getMe even though the config key is unchanged, and restart the loop once the
   * identity resolves - otherwise a transient getMe failure at boot leaves the
   * agent permanently degraded.
   */
  selfUnresolved: boolean;
}

/** codeName -> the agent's live ingest loop. */
const ingests = new Map<string, IngestEntry>();

/**
 * Stable fingerprint of the ingest's inputs. Any change (token rotation, peer
 * roster change, allowlist edit) yields a different key and forces a restart.
 * Hashed rather than stored raw so the bot token never reaches a log line or a
 * heap dump of this map. `self` (getMe) is NOT part of it - it derives from the
 * bot token, so an unchanged token means an unchanged identity, and keying on
 * params alone lets the already-running case short-circuit BEFORE getMe.
 */
function fingerprint(params: TelegramIngestParams): string {
  return createHash('sha256')
    .update(
      JSON.stringify({
        botToken: params.botToken,
        allowedChats: [...params.allowedChats].sort(),
        peerConfig: params.peerConfig,
        ackReaction: params.ackReaction ?? null,
        skipReaction: params.skipReaction ?? null,
      }),
    )
    .digest('hex');
}

/**
 * File-backed durable getUpdates cursor. Re-implements packages/mcp's
 * telegram-offset-store semantics (apps/cli must not import packages/mcp): a
 * missing / corrupt / unsafe file degrades to 0 (legacy "give me everything you
 * still hold"), and a write error is swallowed so a disk fault can't crash the
 * poll loop. The opencode agent has NO telegram MCP, so there is no second writer
 * to race; the file is nonetheless written atomically (tmp + rename).
 */
function createFileOffsetStore(
  codeName: string,
  log: (m: string) => void,
  currentBotId: number | null,
): TelegramOffsetStore {
  let dir: string | null;
  let filePath: string | null;
  try {
    dir = getFramework('opencode').getAgentDir(codeName);
    filePath = join(dir, 'telegram-getupdates-offset-opencode.json');
  } catch {
    dir = null;
    filePath = null;
  }
  return {
    load() {
      if (!filePath) return 0;
      try {
        const parsed = JSON.parse(readFileSync(filePath, 'utf-8')) as { offset?: unknown; bot_id?: unknown };
        // Bot-swap guard: getUpdates offsets (update_ids) are per-bot, so a
        // cursor written by a DIFFERENT bot must not be reused - resuming from it
        // would silently skip the new bot's backlog. Only enforce when BOTH ids
        // are known; a legacy file without bot_id, or an unresolved identity, is
        // accepted as before (backward-compatible).
        if (
          currentBotId != null &&
          typeof parsed?.bot_id === 'number' &&
          parsed.bot_id !== currentBotId
        ) {
          log(`[telegram-ingest:${codeName}] offset cursor belongs to a different bot; ignoring (bot swap)`);
          return 0;
        }
        const offset = parsed?.offset;
        if (typeof offset === 'number' && Number.isFinite(offset) && offset > 0) {
          const normalized = Math.floor(offset);
          // Reject precision-lost magnitudes (> MAX_SAFE_INTEGER): update_ids
          // never approach 2^53, so a value this large is corruption.
          if (Number.isSafeInteger(normalized) && normalized > 0) return normalized;
        }
      } catch {
        /* missing / corrupt - fall through to 0 (legacy behaviour) */
      }
      return 0;
    },
    save(offset: number) {
      if (!filePath || !dir) return;
      const tmpPath = `${filePath}.${process.pid}.${randomUUID()}.tmp`;
      try {
        mkdirSync(dir, { recursive: true, mode: 0o700 });
        writeFileSync(
          tmpPath,
          JSON.stringify({
            offset,
            // Stamp the owning bot so a later token swap invalidates this cursor.
            ...(currentBotId != null ? { bot_id: currentBotId } : {}),
            updated_at: new Date().toISOString(),
          }),
          { mode: 0o600 },
        );
        renameSync(tmpPath, filePath);
      } catch (err) {
        log(`[telegram-ingest:${codeName}] offset persist failed: ${err instanceof Error ? err.message : String(err)}`);
        try {
          if (existsSync(tmpPath)) unlinkSync(tmpPath);
        } catch {
          /* ignore */
        }
      }
    },
  };
}

export interface EnsureTelegramIngestOptions {
  codeName: string;
  agentId: string;
  log: (msg: string) => void;
  /** Test seams. */
  fetchImpl?: FetchLike;
  /** Test seam: override the materialized Telegram env lookup. */
  readEnv?: (codeName: string, channelId: string) => Record<string, string> | null;
  /** Test seam: override the durable offset store. */
  offsetStore?: TelegramOffsetStore;
}

/**
 * Ensure the agent has exactly one Telegram ingest loop matching its current
 * config. Idempotent: a no-op when a loop is already running with the same
 * params, so it is safe to call on every poll tick. Never throws - a resolve
 * failure degrades to "no Telegram ingest for this agent", not a manager crash.
 */
export async function ensureOpencodeTelegramIngest(opts: EnsureTelegramIngestOptions): Promise<void> {
  const { codeName, agentId, log } = opts;
  const readEnv = opts.readEnv ?? readChannelServerEnv;

  let params: TelegramIngestParams | null;
  try {
    const env = readEnv(codeName, 'telegram');
    if (!env) {
      // No telegram MCP entry: the channel was never provisioned or was removed.
      stopOpencodeTelegramIngest(codeName, log);
      return;
    }
    params = resolveTelegramIngestParamsFromEnv(env);
  } catch (err) {
    stopOpencodeTelegramIngest(codeName, log);
    log(`[telegram-ingest:${codeName}] not started: ${err instanceof Error ? err.message : String(err)}`);
    return;
  }

  if (!params) {
    // No bot token: polling is impossible. Not an error.
    stopOpencodeTelegramIngest(codeName, log);
    return;
  }

  const key = fingerprint(params);
  const existing = ingests.get(codeName);
  // Steady-state host: already running, config unchanged, AND identity resolved.
  // A loop still flagged selfUnresolved falls through to retry getMe below.
  if (existing?.key === key && !existing.selfUnresolved) return;

  const { self, authFailed } = await resolveTelegramBotIdentity(params.botToken, opts.fetchImpl);
  if (authFailed) {
    // A definitively-rejected token can only spin on getUpdates 401. Leave no loop.
    stopOpencodeTelegramIngest(codeName, log);
    log(`[telegram-ingest:${codeName}] not started: Telegram bot token rejected (needs reconnect)`);
    return;
  }

  // Same config, previously degraded (selfUnresolved): only restart once the
  // identity actually resolves. Still unresolved => leave the running degraded
  // loop in place rather than churning stop/start it on every poll tick.
  if (existing?.key === key && self.bot_id === null) return;

  if (self.bot_id === null) {
    // Transient getMe failure. Start anyway and degrade: peer classification is
    // weaker without our identity (bots get self_resolution_pending), but the
    // human path is unaffected and a working channel beats no channel.
    log(`[telegram-ingest:${codeName}] bot identity unresolved; peer classification degraded`);
  }

  if (existing) {
    existing.handle.stop();
    ingests.delete(codeName);
    log(
      existing.key === key
        ? `[telegram-ingest:${codeName}] bot identity resolved; restarting ingest`
        : `[telegram-ingest:${codeName}] config changed; restarting ingest`,
    );
  }

  const handle = startTelegramIngest({
    codeName,
    agentId,
    botToken: params.botToken,
    allowedChats: params.allowedChats,
    peerConfig: params.peerConfig,
    self,
    inject: (msg) => injectOpencodeMessage(codeName, msg, { awaitReply: true }),
    log,
    offsetStore: opts.offsetStore ?? createFileOffsetStore(codeName, log, self.bot_id),
    ...(params.ackReaction ? { ackReaction: params.ackReaction } : {}),
    ...(params.skipReaction ? { skipReaction: params.skipReaction } : {}),
    ...(opts.fetchImpl ? { fetchImpl: opts.fetchImpl } : {}),
  });
  ingests.set(codeName, { key, handle, selfUnresolved: self.bot_id === null });
  log(
    `[telegram-ingest:${codeName}] ingest started (peer_mode=${params.peerConfig.peer_agent_mode}, allowlist=${params.allowedChats.size})`,
  );
}

/** Stop and forget an agent's ingest loop. Safe to call when none is running. */
export function stopOpencodeTelegramIngest(codeName: string, log?: (msg: string) => void): void {
  const entry = ingests.get(codeName);
  if (!entry) return;
  entry.handle.stop();
  ingests.delete(codeName);
  log?.(`[telegram-ingest:${codeName}] ingest stopped`);
}

/** Stop every ingest loop (manager shutdown / drain). */
export function stopAllOpencodeTelegramIngests(log?: (msg: string) => void): void {
  for (const codeName of [...ingests.keys()]) stopOpencodeTelegramIngest(codeName, log);
}

/** Test/diagnostic seam: which agents currently have a live ingest loop. */
export function activeTelegramIngestAgents(): string[] {
  return [...ingests.keys()];
}
